import { Box, CircularProgress, Portal, Snackbar } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useEffect, useState } from 'react';
import { hasIOSNativeDownloadHandler } from 'utils/download-file';
import { isIOS } from 'utils/is-ios';
import { downloadPdf, PdfPage, pdfDownloadErrorMessage } from 'utils/market-pdf';
import { ButtonColor } from 'generated/graphql';
import DownloadButton, { ButtonSizes, IconPosition } from './DownloadButton';

// Re-exported for consumers that import these enums from DownloadPdfButton
export { ButtonSizes, IconPosition };

const DownloadPdfButton = ({
  page,
  isSmall = false,
  size,
  hasIcon = false,
  iconOnly = false,
  iconPosition = IconPosition.Right,
  buttonName = 'Download PDF',
  color = ButtonColor.Secondary,
  variant = 'contained',
  noPadding = false,
}: {
  page: PdfPage;
  buttonName?: string;
  isSmall?: boolean;
  size?: ButtonSizes;
  hasIcon?: boolean;
  iconOnly?: boolean;
  iconPosition?: IconPosition;
  color?: ButtonColor | 'inherit';
  variant?: 'contained' | 'outlined';
  noPadding?: boolean;
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDownloading, setDownloading] = useState(false);
  const isIOSDevice = isIOS();

  // Listen for download status messages from Swift (iOS only)
  useEffect(() => {
    if (!isIOSDevice) return;

    const messageHandler = (event: MessageEvent) => {
      // WKWebView safety check
      if (event.source !== window) return;
      if (!event.data || typeof event.data !== 'object') return;

      const { action } = event.data as {
        action?: 'downloadComplete' | 'downloadError';
      };

      if (!action) return;

      if (action === 'downloadComplete') {
        setDownloading(false);
      }

      if (action === 'downloadError') {
        setDownloading(false);
        setErrorMessage(pdfDownloadErrorMessage(null));
      }
    };

    window.addEventListener('message', messageHandler);
    return () => window.removeEventListener('message', messageHandler);
  }, [isIOSDevice]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      setErrorMessage(null);
      await downloadPdf(page);
      // In the Nile iOS app the native bridge finishes the download and the
      // message listener in useEffect clears the loading state.
      if (!hasIOSNativeDownloadHandler()) {
        setDownloading(false);
      }
    } catch (error) {
      setDownloading(false);
      setErrorMessage(pdfDownloadErrorMessage(error));
    }
  };

  return (
    <>
      <DownloadButton
        onDownload={handleDownload}
        isLoading={isDownloading}
        buttonName={buttonName}
        isSmall={isSmall}
        size={size}
        hasIcon={hasIcon}
        iconOnly={iconOnly}
        iconPosition={iconPosition}
        color={color}
        variant={variant}
        noPadding={noPadding}
      />
      {/* Full-screen overlay loader for iOS - Portal ensures it renders above all headers */}
      {isIOSDevice && isDownloading && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <CircularProgress size={40} sx={{ color: 'white' }} />
          </Box>
        </Portal>
      )}
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={errorMessage !== null}
        autoHideDuration={3000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DownloadPdfButton;
